import type { Ref } from "vue";
import { computed, reactive, ref, watch } from "vue";

import type { Controller, OrgId, ReleasableRef } from "@ctrl/controller-rpc";

import type { BaseLogger } from "@/components/inject";
import type { UseProfile } from "@/composables/useProfile";
import { bindAsync, releaseBestEffort, withRelease } from "@/ref";
import { isRecord, property } from "@/utils/unknown";

import { applyAgentInstructionPlaceholders, getAgentTemplate } from "./prompt";

const CUSTOM_AGENT_STORAGE_KEY = "routine:custom-agents";
type StoredCustomAgent = { name: string; avatar: string };

function loadCustomOverrides(): Record<string, StoredCustomAgent> {
  try {
    const raw = localStorage.getItem(CUSTOM_AGENT_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return {};
    const overrides: Record<string, StoredCustomAgent> = {};
    for (const [agentId, value] of Object.entries(parsed)) {
      if (
        isRecord(value) &&
        typeof value.name === "string" &&
        typeof value.avatar === "string"
      ) {
        overrides[agentId] = { name: value.name, avatar: value.avatar };
      }
    }
    return overrides;
  } catch {
    return {};
  }
}

/** Read the stored name/avatar for a user-created agent by its ID. */
export function getCustomAgentOverride(
  agentId: string
): StoredCustomAgent | undefined {
  return loadCustomOverrides()[agentId];
}

function persistCustomOverrides(
  overrides: Record<string, StoredCustomAgent>
): void {
  try {
    localStorage.setItem(CUSTOM_AGENT_STORAGE_KEY, JSON.stringify(overrides));
  } catch {
    // ignore
  }
}

/** Merge one entry into persisted custom agent name/avatar cache (same store as `create`). */
function mergeCustomAgentStorage(
  agentId: string,
  entry: StoredCustomAgent
): void {
  const overrides = loadCustomOverrides();
  overrides[agentId] = entry;
  persistCustomOverrides(overrides);
}

/**
 * Create a user-defined agent (`custom-*` key). The controller persists the
 * name during creation; the avatar remains a frontend-only override until the
 * generated agent contract supports it.
 */
export async function createCustomAgent(
  controller: Controller,
  organizationId: OrgId,
  name: string,
  avatarKey: string,
  instructionsMarkdown?: string
): Promise<string> {
  const key = `custom-${Date.now()}`;
  const md = instructionsMarkdown ?? "";
  const agentId = await controller.ai.agent.create(
    organizationId,
    key,
    name,
    md
  );
  mergeCustomAgentStorage(agentId, {
    name,
    avatar: avatarKey,
  });
  return agentId;
}

/*
 * Types
 */

export type AgentTag = "System";

/** Unique string key for an agent in the local catalog. */
export type CatalogKey = string;

export type CatalogAgent = {
  key: CatalogKey;
  name: string;
  description: string;
  avatar: string;
  tag?: AgentTag;
  instructions?: string;
  examples?: string[];
};

export type ActiveAgent = {
  /** Object ID (workspace_id:table_id:row_id) */
  id: string;
  /** Display name from the agent object ("Name"). */
  name?: string;
  /** Avatar key (e.g. "spark"). Pending controller support for the `avatar` field. */
  avatar?: string;
};

/** Reactive active-agents map returned by `useAgents().active`. */
export type ActiveAgents = Record<OrgId, ActiveAgent[] | undefined>;

export type AgentCatalog = Record<string, CatalogAgent>;

/**
 * Catalog entries: metadata and UI starter suggestions. Agent **instructions** live in
 * `prompts/<key>.prompt`.
 */
const AGENT_CATALOG: AgentCatalog = {
  assistant: {
    key: "assistant",
    name: "Assistant",
    description: "Personal Assistant",
    avatar: "",
    tag: "System",
  },
  "operations-manager": {
    key: "operations-manager",
    name: "Andrew",
    description: "Operations Manager",
    avatar: getAvatarUrl("6"),
    examples: [
      "Set up the organization and workspaces",
      "Create databases to support use cases",
      "Create & manage automations to build workflows",
      "Maintain the glue between all the pieces for the company to run smoothly",
    ],
  },
  "social-media-specialist": {
    key: "social-media-specialist",
    name: "Bob",
    description: "Social Media Specialist",
    avatar: getAvatarUrl("2"),
    examples: [
      "Turn my notes from today into a LinkedIn post",
      "Write 3 tweets about the event I have tomorrow",
      "Draft a product update post based on tasks we completed this week",
      "Create a content calendar from my upcoming events",
    ],
  },
  "lead-generator": {
    key: "lead-generator",
    name: "Alex",
    description: "Lead Generator",
    avatar: getAvatarUrl("3"),
    examples: [
      "Add 10 leads to my database from today's event attendees",
      "Draft a cold email for each contact in my leads table",
      "Create a follow-up task for every lead I met this week",
      "Summarize my pipeline notes and flag stale contacts",
    ],
  },
  "executive-assistant": {
    key: "executive-assistant",
    name: "Maria",
    description: "Executive Assistant",
    avatar: getAvatarUrl("4"),
    examples: [
      "Summarize my calendar for this week and flag conflicts",
      "Create tasks from the action items in my last meeting notes",
      "Draft a follow-up email based on today's event",
      "Reschedule my tasks around the events added this morning",
    ],
  },
  "community-manager": {
    key: "community-manager",
    name: "Elena",
    description: "Community Manager",
    avatar: getAvatarUrl("5"),
    examples: [
      "Summarize feedback from my notes and add themes to my database",
      "Create tasks for each community request logged this week",
      "Draft announcements for the events on my calendar next week",
      "Turn my community call notes into a recap post",
    ],
  },
  "content-writer": {
    key: "content-writer",
    name: "Lily",
    description: "Content Writer",
    avatar: getAvatarUrl("1"),
    examples: [
      "Turn my notes from this week into a draft blog post",
      "Write a newsletter based on tasks and events from this month",
      "Expand the bullet points in my notes into a full article",
      "Pull highlights from my database and write a product update",
    ],
  },
  "customer-support": {
    key: "customer-support",
    name: "Clara",
    description: "Customer Support",
    avatar: getAvatarUrl("7"),
    examples: [
      "Create a task for each open support issue in my database",
      "Draft a response based on the notes from this customer call",
      "Summarize all unresolved issues logged in my support table",
      "Schedule a follow-up event for tickets marked as pending",
    ],
  },
  "project-manager": {
    key: "project-manager",
    name: "Taylor",
    description: "Project Manager",
    avatar: getAvatarUrl("8"),
    examples: [
      "Break down my project note into tasks and add them to my board",
      "Summarize completed tasks this week and flag what's overdue",
      "Create a kickoff event and agenda from my project notes",
      "Update my project database with progress from today's tasks",
    ],
  },
};

/*
 * Private fnctions
 */

function getAvatarUrl(name: string): string {
  try {
    return new URL(`./avatars/${name.toLowerCase()}.png`, import.meta.url).href;
  } catch {
    return "";
  }
}

function listCatalogAgents(): Record<string, CatalogAgent> {
  return AGENT_CATALOG;
}

/** Get avatar URL for an agent by its catalog key, or empty string if not found. */
export function getAgentAvatar(key: string): string {
  return AGENT_CATALOG[key]?.avatar ?? "";
}

/** Get display name for an agent by its catalog key, or empty string if not found. */
export function getAgentName(key: string): string {
  return AGENT_CATALOG[key]?.name ?? "";
}

/*
 * Public functions
 */

export type UseAgents = {
  catalog: AgentCatalog;
  /** Reactive active agents, keyed by organization id. */
  active: ActiveAgents;
  initialize: (organizationId: OrgId) => Promise<void>;
  /**
   * Create a new Agent object instance from a catalog entry. Multiple instances are allowed.
   * Instruction text from the `.prompt` file is processed with placeholders: `ORGANIZATION_ID`
   * is always set to `organizationId`; pass `placeholders` to add or override tokens.
   */
  instantiate: (
    organizationId: OrgId,
    key: CatalogKey,
    placeholders?: Record<string, string>
  ) => Promise<string | undefined>;
  /**
   * Create a user-defined agent with a custom name and avatar.
   */
  create: (
    organizationId: OrgId,
    name: string,
    avatarKey: string,
    instructionsMarkdown?: string
  ) => Promise<string | undefined>;
  /** Destroy a single agent instance by agentId. */
  destroy: (organizationId: OrgId, agentId: string) => Promise<void>;
  /** List active agent instances for a catalog entry (may be empty). */
  listActive: (organizationId: OrgId, key: CatalogKey) => ActiveAgent[];
  /** Count active agent instances for a catalog entry. */
  countActive: (organizationId: OrgId, key: CatalogKey) => number;
  /** List active agents whose key is not in the catalog (user-created custom agents). */
  listCustom: (organizationId: OrgId) => ActiveAgent[];
};

export async function useAgents(
  controller: Controller,
  baseLogger: BaseLogger,
  profile: Ref<UseProfile>
): Promise<Readonly<ReleasableRef<UseAgents>>> {
  const logger = baseLogger.forModule("useAgents");
  const catalogAgents = listCatalogAgents();
  let released = false;

  type InternalActiveAgent = Required<ActiveAgent> & { key: CatalogKey };

  type OrgState = {
    activeAgents: Readonly<ReleasableRef<InternalActiveAgent[] | undefined>>;
    release: () => void;
  };

  const active: ActiveAgents = reactive({});
  const orgStates = new Map<OrgId, OrgState>();
  const customOverrides = ref<Record<string, StoredCustomAgent>>(
    loadCustomOverrides()
  );

  const toIds = (value: unknown): string[] =>
    Array.isArray(value)
      ? value.filter((v): v is string => typeof v === "string")
      : [];

  function release(): void {
    if (released) return;
    released = true;
    for (const state of orgStates.values()) state.release();
    orgStates.clear();
    for (const orgId of Object.keys(active)) delete active[orgId];
  }

  const orgIdsRef = computed(() =>
    profile.value.organizations.map((o) => o.id)
  );

  const createOrgState = async (
    organizationId: OrgId
  ): Promise<OrgState | undefined> => {
    const agentIdsRef = await controller.ai.agent.all(organizationId);

    // When agent IDs change, re-fetch all agent refs and recompute the list.
    const activeAgents = await bindAsync(agentIdsRef, async (agentIds) => {
      const ids = toIds(agentIds);
      const agentRefs = await Promise.all(
        ids.map((id) => controller.ai.agent.get(organizationId, id))
      );

      // Reading .name, .key, and .avatar from each ref makes this computed
      // reactive to individual agent updates, not just ID-list changes.
      const resolved = computed<InternalActiveAgent[]>(() =>
        agentRefs.flatMap((r, i) => {
          const data = r.value;
          const key = typeof data?.key === "string" ? data.key : undefined;
          if (!key || !data) return [];
          const catalogEntry = AGENT_CATALOG[key];
          const agentId = ids[i];
          const override = customOverrides.value[agentId];
          const avatarValue = property(data, "avatar");
          const avatar =
            typeof avatarValue === "string" ? avatarValue : override?.avatar;
          return [
            {
              id: agentId,
              name: data.name ?? catalogEntry?.name ?? override?.name ?? key,
              avatar,
              key,
            },
          ];
        })
      );

      return withRelease(resolved, () => {
        for (const r of agentRefs) releaseBestEffort(r);
      });
    });

    const stopSync = watch(
      activeAgents,
      (value) => {
        active[organizationId] = (value ?? []).map(({ id, name, avatar }) => ({
          id,
          name,
          avatar,
        }));
      },
      { immediate: true }
    );

    return {
      activeAgents,
      release: () => {
        stopSync();
        if (active[organizationId] !== undefined) delete active[organizationId];
        releaseBestEffort(activeAgents);
        releaseBestEffort(agentIdsRef);
      },
    };
  };

  let syncChain = Promise.resolve();
  const stopOrgIdsSync = watch(
    orgIdsRef,
    (organizationIds) => {
      const ids: OrgId[] = toIds(organizationIds);
      syncChain = syncChain.then(async () => {
        if (released) return;

        const desired = new Set(ids);

        // Seed active entry immediately so callers don't see a missing key.
        for (const orgId of ids) {
          if (active[orgId] == null) active[orgId] = [];
        }

        for (const orgId of Array.from(orgStates.keys())) {
          if (!desired.has(orgId)) {
            orgStates.get(orgId)?.release();
            orgStates.delete(orgId);
          }
        }

        for (const orgId of ids) {
          if (orgStates.has(orgId)) continue;
          try {
            const state = await createOrgState(orgId);
            if (state) orgStates.set(orgId, state);
          } catch (error) {
            logger.error(
              `failed to initialize agents for organization "${orgId}"`,
              error
            );
          }
        }
      });
    },
    { immediate: true }
  );

  const getState = (organizationId: OrgId): OrgState => {
    const state = orgStates.get(organizationId);
    if (!state) {
      throw new Error(
        `[agents] agents not ready for organization "${organizationId}"`
      );
    }
    return state;
  };

  const instantiate = async (
    organizationId: OrgId,
    key: CatalogKey,
    placeholders?: Record<string, string>
  ): Promise<string | undefined> => {
    const agent = catalogAgents[key];
    if (!agent) {
      throw new Error(`[agents] agent with id "${key}" not found in catalog`);
    }

    try {
      // Get the agent template (markdown file from frontend)
      let instructionsText =
        getAgentTemplate(agent.key) || (agent.instructions ?? "");

      const mergedPlaceholders: Record<string, string> = {
        ORGANIZATION_ID: organizationId,
        ...placeholders,
      };
      instructionsText = applyAgentInstructionPlaceholders(
        instructionsText,
        mergedPlaceholders
      );

      // Call controller RPC to create agent instance
      const result = await controller.ai.agent.create(
        organizationId,
        agent.key,
        agent.name,
        instructionsText
      );

      return result;
    } catch (error) {
      throw new Error(
        `[agents] failed to create agent ${key} (org=${organizationId}): ${error}`
      );
    }
  };

  const destroy = async (
    organizationId: OrgId,
    agentId: string
  ): Promise<void> => {
    if (!agentId) return;

    try {
      await controller.ai.agent.delete(organizationId, agentId);
      const { [agentId]: _, ...rest } = customOverrides.value;
      customOverrides.value = rest;
      persistCustomOverrides(customOverrides.value);
    } catch (error) {
      throw new Error(`[agents] failed to delete agent ${agentId}: ${error}`);
    }
  };

  const initialize = async (organizationId: OrgId): Promise<void> => {
    // Ensure all system agents are enabled.
    for (const [key, agent] of Object.entries(catalogAgents)) {
      if (agent.tag !== "System") continue;
      try {
        // Ensure at least one instance exists.
        if (countActive(organizationId, key) === 0) {
          await instantiate(organizationId, key);
        }
      } catch (error) {
        logger.error(
          `failed to enable system agent ${key} (org=${organizationId})`,
          error
        );
      }
    }
  };

  const listActive = (
    organizationId: OrgId,
    key: CatalogKey
  ): ActiveAgent[] => {
    const state = getState(organizationId);
    return (state.activeAgents.value ?? [])
      .filter((a) => a.key === key)
      .map(({ id, name, avatar }) => ({ id, name, avatar }));
  };

  const countActive = (organizationId: OrgId, key: CatalogKey): number => {
    return listActive(organizationId, key).length;
  };

  const listCustom = (organizationId: OrgId): ActiveAgent[] => {
    const state = getState(organizationId);
    return (state.activeAgents.value ?? [])
      .filter((a) => !catalogAgents[a.key])
      .map(({ id, name, avatar }) => ({ id, name, avatar }));
  };

  const create = async (
    organizationId: OrgId,
    name: string,
    avatarKey: string,
    instructionsMarkdown?: string
  ): Promise<string | undefined> => {
    try {
      const agentId = await createCustomAgent(
        controller,
        organizationId,
        name,
        avatarKey,
        instructionsMarkdown
      );

      customOverrides.value = {
        ...customOverrides.value,
        [agentId]: { name, avatar: avatarKey },
      };
      return agentId;
    } catch (error) {
      throw new Error(
        `[agents] failed to create agent "${name}" (org=${organizationId}): ${error}`
      );
    }
  };

  const api: UseAgents = {
    catalog: catalogAgents,
    active,
    initialize,
    instantiate,
    create,
    destroy,
    listActive,
    countActive,
    listCustom,
  };
  return withRelease(ref(api), () => {
    stopOrgIdsSync();
    release();
  });
}
